import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, MalouMetric, PlatformKey } from '@malou-io/package-utils';

import { CommunityChartData } from '#modules/statistics-social-networks/models/social-networks.interfaces';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { formatDateToISO, getDaysFromCurrentRange } from '#shared/helpers/index';
import { DatesAndPeriod } from '#shared/models/index';

interface FollowersData {
    dates: DatesAndPeriod;
    followers: CommunityChartData;
}
interface Options {
    platformKeys: PlatformKey[];
}

@Injectable()
export class FollowersCsvInsightsV2Service extends AbstractCsvService<FollowersData, Options> {
    private readonly _statisticsStore = inject(StatisticsStore);

    protected override _getData$(): Observable<FollowersData> {
        return of({
            dates: this._statisticsStore.filters().dates,
            followers: this._statisticsStore.followersDataV2(),
        });
    }

    protected override _isDataValid(data: FollowersData, options: Options): boolean {
        if (!data?.dates?.startDate) {
            return false;
        }
        if (options.platformKeys.length === 1) {
            const platformKey = options.platformKeys[0];
            return !!data?.followers?.[platformKey];
        }
        return !!data?.followers?.facebook && !!data?.followers?.instagram && !!data?.followers?.tiktok;
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return ['Date', 'Followers'];
    }

    protected override _getCsvDataRows({ dates, followers }: FollowersData, { platformKeys }: Options): CsvAsStringArrays {
        const { startDate, endDate } = dates;
        if (!startDate || !endDate) {
            return [];
        }
        const days = getDaysFromCurrentRange(startDate, endDate);
        return days.map((day) => {
            const localDate = day?.toLocaleDateString();
            const formattedDay = formatDateToISO(day);
            const platformsFollowers: number = platformKeys
                .map((platformKey) => followers[platformKey]?.[MalouMetric.FOLLOWERS]?.[formattedDay] ?? 0)
                .reduce((acc, next) => acc + next, 0);
            const platformsFollowersString = platformsFollowers ? platformsFollowers.toString() : '-';
            return [localDate, platformsFollowersString];
        });
    }
}
